import { Breadcrumb, Space, Typography } from 'antd';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { mediaDown, space, typography } from '@/design/tokens.js';

const { Title, Text } = Typography;

export interface Crumb {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  title: string;
  /** One short sentence saying what this screen is for. */
  description?: string;
  breadcrumbs?: Crumb[];
  /** Primary and secondary actions, right-aligned on desktop. */
  actions?: ReactNode;
  /** Tabs or filters that belong to the page rather than the content. */
  children?: ReactNode;
}

/**
 * The top of every page.
 *
 * One component so that every screen answers the same three questions in the
 * same place: where am I, what is this, what can I do here. That consistency
 * is Nielsen's "recognition rather than recall" — the user learns the layout
 * once and it holds everywhere.
 *
 * On narrow screens the actions drop below the title rather than squeezing it,
 * because a truncated page title is worse than a taller header.
 */
export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  children,
}: PageHeaderProps) {
  return (
    <header
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: space[3],
        marginBottom: space[6],
      }}
    >
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <Breadcrumb
          items={breadcrumbs.map((crumb) => ({
            title: crumb.to ? <Link to={crumb.to}>{crumb.label}</Link> : crumb.label,
          }))}
          style={{ fontSize: typography.size.sm }}
        />
      ) : null}

      <div className="orbit-page-header__bar">
        <div style={{ minWidth: 0 }}>
          <Title
            level={1}
            style={{
              margin: 0,
              fontSize: typography.size['2xl'],
              fontWeight: typography.weight.semibold,
              letterSpacing: typography.letterSpacing.tighter,
              lineHeight: typography.lineHeight.tight,
            }}
          >
            {title}
          </Title>
          {description ? (
            <Text type="secondary" style={{ display: 'block', marginTop: space[1] }}>
              {description}
            </Text>
          ) : null}
        </div>

        {actions ? (
          <Space size={space[2]} wrap className="orbit-no-print">
            {actions}
          </Space>
        ) : null}
      </div>

      {children}

      <style>{`
        .orbit-page-header__bar {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: ${space[4]}px;
        }
        ${mediaDown('md')} {
          .orbit-page-header__bar {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>
    </header>
  );
}
